/**
 * Scene geometry shared by the painters, the renderer and the particles.
 *
 * Painters work in CSS pixels. Positions in theme data are fractions of the
 * viewport; object sizes are "scene units" (see packages/core/src/themes/scene.ts)
 * so a tree keeps its proportions on a portrait phone and a wide desktop.
 */

export interface SceneSize {
  /** Viewport size in CSS px. */
  width: number;
  height: number;
  /** One scene unit in CSS px. */
  unit: number;
  /**
   * Extra px painted past the left and right edges so layers can sway
   * sideways (parallax) without showing a gap. Full-width painters cover
   * [-bleed, width + bleed].
   */
  bleed: number;
}

/** 1 unit = 0.8·√(w·h), capped at 0.85·h so very wide screens don't get giant trees. */
export function sceneUnit(width: number, height: number): number {
  return Math.max(1, Math.min(0.8 * Math.sqrt(width * height), 0.85 * height));
}

/** Largest parallax sway (at depth 1), in CSS px. */
export function maxSway(width: number): number {
  return clamp(width * 0.012, 3, 14);
}

export function sceneSize(width: number, height: number): SceneSize {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  return { width: w, height: h, unit: sceneUnit(w, h), bleed: Math.ceil(maxSway(w)) + 2 };
}

/**
 * Horizontal parallax offset (CSS px) of a layer at `depth` after `time`
 * seconds: a slow, organic sway (two incommensurate sines, ~26 s and ~61 s),
 * so near layers drift a few px against far ones. Depth 0 never moves.
 */
export function parallaxOffset(time: number, depth: number, size: SceneSize): number {
  if (depth <= 0) return 0;
  const wave = 0.75 * Math.sin((time * 2 * Math.PI) / 26) + 0.25 * Math.sin((time * 2 * Math.PI) / 61 + 1.3);
  return wave * depth * maxSway(size.width);
}

/**
 * Depths are quantised to eighths so neighbouring static layers with nearly
 * the same depth share one cached bitmap (fewer full-screen blits per frame).
 */
export function quantizeDepth(depth: number | undefined): number {
  return Math.round(clamp(depth ?? 0, 0, 1) * 8) / 8;
}

/** Outline width for the cartoon rims: chunky on big screens, never below a hairline. */
export function rimWidth(size: SceneSize): number {
  return clamp(size.unit * 0.0045, 0.75, 3.5);
}

/** Device-pixel ratio used for the canvas backing store (capped at 2 for memory and fill-rate). */
export function pixelRatio(devicePixelRatio: number | undefined): number {
  const dpr = devicePixelRatio && Number.isFinite(devicePixelRatio) ? devicePixelRatio : 1;
  return clamp(dpr, 1, 2);
}

/** Backing-store pixel budget for one full-screen canvas (≈ 1920×1350 device px). */
export const MAX_BACKING_PIXELS = 2_600_000;

/**
 * Pixel ratio actually used for the background canvases: the device ratio
 * (capped at 2), lowered on very large viewports so each full-screen bitmap
 * stays within MAX_BACKING_PIXELS. The art is soft vector shapes, so a
 * slightly lower ratio on a big monitor is invisible, while the memory and
 * fill-rate savings are large. Never below 0.5.
 */
export function backingRatio(width: number, height: number, devicePixelRatio: number | undefined): number {
  const area = Math.max(1, width) * Math.max(1, height);
  return clamp(Math.min(pixelRatio(devicePixelRatio), Math.sqrt(MAX_BACKING_PIXELS / area)), 0.5, 2);
}

/**
 * Evenly spread `count` items over [from, to] (fractions of the width), each
 * nudged by up to ±35 % of its slot using `jitter` values in [0, 1).
 */
export function spreadPositions(count: number, spread: readonly [number, number], jitter: (i: number) => number): number[] {
  const [from, to] = spread;
  const n = Math.max(0, Math.floor(count));
  const slot = (to - from) / Math.max(1, n);
  return Array.from({ length: n }, (_, i) => from + slot * (i + 0.5 + (jitter(i) - 0.5) * 0.7));
}

/** Wrap `v` into [min, max) — used for drifting clouds, balloons and birds. */
export function wrap(v: number, min: number, max: number): number {
  const span = max - min;
  if (span <= 0) return min;
  return ((((v - min) % span) + span) % span) + min;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Hermite smoothstep of x between edges a and b. */
export function smoothstep(a: number, b: number, x: number): number {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}
